/**
 * Constraints Folder Loader - Loads CHECK and DEFAULT constraints for a table subfolder
 */

import {
  DatabaseExplorerNode,
  MessageTreeNode,
  ConstraintsFolderNode,
  createConstraintNode,
  ConstraintNodeMetadata,
} from '../../explorer/models/DatabaseTreeModels';
import {
  createTableSubfolderEmptyNode,
  createTableSubfolderErrorNode,
} from './objectFolderUtils';
import { getConstraints, ConstraintInfo, formatConstraintDisplayName } from './constraintsService';
import { getEngineForConnection } from './tableExpansion';

export interface LoadConstraintsFolderOptions {
  node: ConstraintsFolderNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  forceReload?: boolean;
}

/**
 * Loads CHECK and DEFAULT constraints for a table when the Constraints folder is expanded
 */
export async function loadConstraintsFolder({
  node,
  updateNode,
  forceReload = false,
}: LoadConstraintsFolderOptions) {
  if (!forceReload && node.children && node.children.length > 0) {
    const firstChild = node.children[0];
    if (firstChild && !['message', 'error'].includes(firstChild.type)) {
      return;
    }
  }

  const { connectionId, database, schema, tableName } = node.metadata;

  if (!database) {
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, '', schema, tableName, 'constraints-folder', node.id, 'Database not specified'
        ),
      ],
      isLoading: false,
    });
    return;
  }

  updateNode(node.id, {
    children: [
      {
        id: `loading-${connectionId}-${database}-${schema}-${tableName}-constraints`,
        parentId: node.id,
        name: 'Loading constraints...',
        type: 'message',
        canHaveChildren: false,
        children: [],
        isExpanded: false,
        icon: 'loading',
        metadata: { connectionId, database },
      } as MessageTreeNode,
    ],
    isLoading: true,
  });

  try {
    const engine = getEngineForConnection(connectionId);
    const result = await getConstraints(connectionId, database, schema, tableName, engine);

    if (result.error) {
      updateNode(node.id, {
        children: [
          createTableSubfolderErrorNode(
            connectionId, database, schema, tableName, 'constraints-folder', node.id, result.error
          ),
        ],
        isLoading: false,
      });
      return;
    }

    if (result.constraints.length === 0) {
      updateNode(node.id, {
        children: [
          createTableSubfolderEmptyNode(
            connectionId, database, schema, tableName, 'constraints-folder', node.id
          ),
        ],
        isLoading: false,
      });
      return;
    }

    const constraintNodes = result.constraints.map((constraint: ConstraintInfo) => {
      const displayName = formatConstraintDisplayName(constraint);
      const metadata: ConstraintNodeMetadata = {
        connectionId,
        database,
        schema,
        tableName,
        constraintName: constraint.name,
        constraintType: constraint.constraintType,
        definition: constraint.definition,
        columnName: constraint.columnName,
      };
      const constraintNode = createConstraintNode(metadata, node.id);
      constraintNode.name = displayName;
      return constraintNode;
    });

    updateNode(node.id, {
      children: constraintNodes,
      isLoading: false,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load constraints';
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, database, schema, tableName, 'constraints-folder', node.id, message
        ),
      ],
      isLoading: false,
    });
  }
}
